import {
  usePaginatedResource,
  useResourceItem,
  useCreateResource,
  useEditResource,
  useDeleteResource,
} from "./useCrudResource"

import {
  getCustomers,
  getCustomer,
  createCustomer,
  editCustomer,
  deleteCustomer,
  getAllCustomers,
  getTrashedCustomers,
  restoreCustomer,
  forceDeleteCustomer,
  getCustomerBySlug,
} from "api/customerApi"

import {
  useIsFetching,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { useMemo } from "react"
import { useFilteredVehicleOptions } from "./stockFilterHook"

export const CUSTOMER_QUERY_KEY = "customer"

// -------------------- FETCH PAGINATED --------------------
export const useCustomers = (params = {}, enabled = true) => {
  return usePaginatedResource(CUSTOMER_QUERY_KEY, getCustomers, params, enabled)
}

// -------------------- FETCH SINGLE ITEM --------------------
export const useCustomer = (id, enabled = true) => {
  return useResourceItem(CUSTOMER_QUERY_KEY, getCustomer, id, enabled)
}

// -------------------- FETCH BY SLUG --------------------
export const useCustomerBySlug = (slug, enabled = true) => {
  return useResourceItem(
    [CUSTOMER_QUERY_KEY, "slug"],
    getCustomerBySlug,
    slug,
    enabled
  )
}

// -------------------- CREATE --------------------
export const useCreateCustomer = () => {
  return useCreateResource(createCustomer, CUSTOMER_QUERY_KEY)
}

// -------------------- EDIT --------------------
export const useEditCustomer = () => {
  return useEditResource(editCustomer, CUSTOMER_QUERY_KEY)
}

// -------------------- DELETE (Soft Delete) --------------------
export const useDeleteCustomer = () => {
  return useDeleteResource(deleteCustomer, CUSTOMER_QUERY_KEY)
}

// -------------------- FORCE DELETE (Permanent Delete) --------------------
export const useForceDeleteCustomer = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: forceDeleteCustomer,
    onSuccess: (_, id) => {
      // Remove from trashed list cache
      queryClient.setQueriesData([CUSTOMER_QUERY_KEY, "trashed"], old => {
        if (!old || !old.data?.data) return old
        const updated = structuredClone(old)
        updated.data.data = updated.data.data.filter(item => item.id !== id)
        updated.data.total = Math.max(0, (updated.data.total || 1) - 1)
        return updated
      })

      // Remove from any cached customer record
      queryClient.removeQueries([CUSTOMER_QUERY_KEY, id])
    },
  })
}

// -------------------- ADDITIONAL --------------------

// ✅ Get all customers without pagination
export const useAllCustomers = (params = {}) => {
  return usePaginatedResource(
    CUSTOMER_QUERY_KEY,
    getAllCustomers,
    { ...params, all: true },
    true
  )
}

// ✅ Get trashed (soft-deleted) customers
export const useTrashedCustomers = (params = {}) => {
  return usePaginatedResource(
    [CUSTOMER_QUERY_KEY, "trashed"],
    getTrashedCustomers,
    params,
    true
  )
}

// ✅ Restore a trashed customer
export const useRestoreCustomer = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: restoreCustomer,
    onSuccess: (res, id) => {
      const restoredItem = res?.data
      if (!restoredItem) return

      // Remove from trashed cache
      queryClient.setQueriesData([CUSTOMER_QUERY_KEY, "trashed"], old => {
        if (!old || !old.data?.data) return old
        const updated = structuredClone(old)
        updated.data.data = updated.data.data.filter(item => item.id !== id)
        updated.data.total = Math.max(0, (updated.data.total || 1) - 1)
        return updated
      })

      // Add to main list cache
      queryClient.setQueriesData([CUSTOMER_QUERY_KEY], old => {
        if (!old || !old.data?.data) return old
        const updated = structuredClone(old)
        updated.data.data = [restoredItem, ...updated.data.data]
        updated.data.total = (updated.data.total || 0) + 1
        return updated
      })

      // Update individual cache
      queryClient.setQueryData([CUSTOMER_QUERY_KEY, id], restoredItem)
    },
  })
}

// Customer info
export const useCustomerInfo = () => {
  const {
    isLoading: optionLoading,
    raw: rawData,
    refetch: refetchOptions,
  } = useFilteredVehicleOptions()

  const customer = rawData?.customer ?? {}

  const handleFromDb = (options, value) => {
    if (typeof value === "number" && typeof Number(value) === "number") {
      return findNameById(options, value)
    }
    return value
  }

  // Prepare options for CustomerForm
  const options = useMemo(
    () => ({
      designations: customer?.designations ?? [],
      departments: customer?.departments ?? [],
      companies: customer?.companies ?? [],
      customerTypes: customer?.customer_types ?? [],
      areas: customer?.customer_areas ?? [],
      cities: customer?.customer_city ?? [],
    }),
    [customer]
  )

  const findNameById = (list, id) =>
    list?.find(item => item.id === id)?.name || "-"

  const {
    designations = [],
    departments = [],
    companies = [],
    customerTypes = [],
    areas = [],
    cities = [],
  } = options

  const infoFormatter = customerInfo => {
    return {
      ...customerInfo,
      name: customerInfo.name || "-",
      mobile: customerInfo.mobile || "-",
      email: customerInfo.email || "-",
      designation_id: handleFromDb(designations, customerInfo.designation_id),
      designation: handleFromDb(designations, customerInfo.designation_id),
      department_id: handleFromDb(departments, customerInfo.department_id),
      department: handleFromDb(departments, customerInfo.department_id),
      company_id: handleFromDb(companies, customerInfo.company_id),
      company: handleFromDb(companies, customerInfo.company_id),
      customer_type_id: handleFromDb(
        customerTypes,
        customerInfo.customer_type_id
      ),
      customer_type: handleFromDb(customerTypes, customerInfo.customer_type_id),
      area_id: handleFromDb(areas, customerInfo.area_id),
      area: handleFromDb(areas, customerInfo.area_id),
      city_id: handleFromDb(cities, customerInfo.city_id),
      city: handleFromDb(cities, customerInfo.city_id),
      full_address: customerInfo.full_address || "-",
    }
  }

  const getCustomerRickText = customerInfo => {
    if (!customerInfo || !Object.keys(customerInfo).length) return ""

    const info = infoFormatter(customerInfo)

    return `
<strong>Name:</strong> ${info.name ?? "" ?? ""}<br/>
<strong>Mobile:</strong> ${info.mobile ?? "" ?? ""}<br/>
<strong>Email:</strong> ${info.email ?? "" ?? ""}<br/>
<strong>Designation:</strong> ${info.designation ?? "" ?? ""}<br/>
<strong>Department:</strong> ${info.department ?? "" ?? ""}<br/>
<strong>Company:</strong> ${info.company ?? "" ?? ""}<br/>
<strong>Customer Type:</strong> ${info.type ?? "" ?? ""}<br/>
<strong>Location:</strong> ${[info.area ?? "" ?? "", info.city ?? "" ?? ""]
      .filter(Boolean)
      .join(", ")}<br/>
<strong>Full Address:</strong> ${info.full_address ?? "" ?? ""}
    `.trim()
  }

  const getQuotationCustomerRichText = (customerInfo = {}) => {
    if (!customerInfo || !Object.keys(customerInfo).length) return ""

    const info = infoFormatter(customerInfo)

    return `${info.name ?? ""}\n${[
      info.designation ?? "",
      info.department ?? "",
    ]
      .filter(v => v)
      .join(", ")
      .trim()}\n${info.company ?? ""}\n${info.full_address ?? ""}\n`.trim()
  }

  const getCustomerInfo = (customerInfo = {}) => {
    if (!customerInfo || !Object.keys(customerInfo).length) return ""

    return infoFormatter(customerInfo)
  }

  return {
    getCustomerRickText,
    getQuotationCustomerRichText,
    getCustomerInfo,
    customer,
    options,
  }
}

// Function version (for programmatic access)
export const useCustomerSearchAPI = ({ all = true, limit = 3 }) => {
  const queryClient = useQueryClient()

  // Function uses React Query cache internally
  const customerSearchAPI = async searchTerm => {
    const queryKey = [
      "customer-searching",
      {
        search: searchTerm,
        all: typeof all === "boolean" ? all : true,
        limit: limit ?? 3,
      },
    ]

    // ✅ 1. Try to get cached data first
    const cached = queryClient.getQueryData(queryKey)
    if (cached) return cached

    // ✅ 2. If not cached, fetch and cache manually
    const data = await queryClient.fetchQuery({
      queryKey,
      queryFn: () => getCustomers({ search: searchTerm, all: true }),
    })

    return data
  }

  // React Query tracks global fetch state for this key
  const isFetching = useIsFetching({ queryKey: ["customer-searching"] }) > 0

  return { customerSearchAPI, loading: isFetching }
}
